import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../App';
import { adminApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, Loader2, Shield } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_confusing-preview/artifacts/gu4q9ocf_trans_white_logo.png";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { adminLogin } = useAuth();
  const [email, setEmail] = useState('super_admin@lekeewallet.com');
  const [password, setPassword] = useState('admin123');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await adminApi.login(email, password);
      adminLogin(response.data.admin, response.data.access_token);
      toast.success('Welcome to Admin Portal');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="p-6 flex items-center justify-center">
        <img src={LOGO_URL} alt="LekeeWallet" className="h-12 w-auto" />
      </div>
      
      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
        <div className="w-full max-w-sm space-y-8">
          {/* Admin Badge */}
          <div className="flex items-center justify-center gap-2 text-primary">
            <Shield className="w-6 h-6" />
            <span className="text-lg font-semibold">Admin Portal</span>
          </div>
          
          {/* Title */}
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Admin Login
            </h1>
            <p className="text-muted-foreground">
              Access the LekeeWallet admin console
            </p>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@lekeewallet.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 bg-secondary border-white/10"
                data-testid="admin-email-input"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 bg-secondary border-white/10 pr-12"
                  data-testid="admin-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full h-12 bg-primary hover:bg-red-600 text-white font-semibold glow-primary"
              disabled={isLoading}
              data-testid="admin-login-btn"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Access Admin Console
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>
          
          {/* Demo credentials */}
          <div className="bg-card border border-white/10 rounded-xl p-4 space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Demo Admin Accounts:</p>
            <div className="text-xs space-y-1 text-muted-foreground">
              <p>Super Admin: super_admin@lekeewallet.com</p>
              <p>Campaign Manager: campaign_manager@lekeewallet.com</p>
              <p>Finance: finance_manager@lekeewallet.com</p>
              <p>Password: admin123</p>
            </div>
          </div>
          
          {/* Back link */}
          <div className="text-center">
            <Link 
              to="/login" 
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Back to User Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
